import { API_BASE } from '../config/api';
import AuthService from './authService';

class NotificationService {
  static API_BASE = API_BASE;

  // Store active timers for cleanup
  static activeTimers = [];

  // Initialize notification service
  static init() {
    // Check if user is logged in before initializing
    if (!AuthService.isAuthenticated()) {
      console.log('NotificationService: No auth token, skipping initialization');
      return;
    }

    this.requestPermission();
    this.loadNotificationPreferences();
    this.scheduleUpcomingReminders();
  }

  // Cleanup method - called on logout
  static cleanup() {
    // Clear all active timers
    this.activeTimers.forEach(timerId => clearTimeout(timerId));
    this.activeTimers = [];
    console.log('NotificationService: Cleaned up all timers');
  }

  // Request notification permission from browser
  static async requestPermission() {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  // Default notification preferences
  static getDefaultPreferences() {
    return {
      activityReminders: {
        enabled: true,
        intervals: ['1day', '2hours'],
        customIntervals: []
      },
      weatherAlerts: {
        enabled: true,
        outdoorActivitiesOnly: true
      },
      eventDiscovery: {
        enabled: true,
        categories: ['concerts', 'festivals', 'family', 'food', 'art', 'sports'],
        frequency: 'weekly',
        radius: 25
      },
      recommendations: {
        enabled: true,
        similarUsers: true,
        trendingActivities: true,
        seasonalSuggestions: true
      },
      quietHours: {
        enabled: true,
        start: '22:00',
        end: '08:00'
      }
    };
  }

  // Get notification preferences from API (with localStorage fallback for guests)
  static async getNotificationPreferencesAsync() {
    if (!AuthService.isAuthenticated()) {
      // Guest users: use localStorage
      const saved = localStorage.getItem('notificationPreferences');
      return saved ? { ...this.getDefaultPreferences(), ...JSON.parse(saved) } : this.getDefaultPreferences();
    }

    try {
      const response = await fetch(`${NotificationService.API_BASE}/api/social/notification-preferences`, {
        headers: AuthService.getJsonHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          return { ...this.getDefaultPreferences(), ...data.preferences };
        }
      }

      // Fallback to defaults if API fails
      return this.getDefaultPreferences();
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  // Synchronous version for backwards compatibility (returns defaults, use async version for API)
  static getNotificationPreferences() {
    // For synchronous calls, return cached or defaults
    // The async version should be preferred
    const cached = this._cachedPreferences;
    if (cached) return cached;

    const saved = localStorage.getItem('notificationPreferences');
    return saved ? { ...this.getDefaultPreferences(), ...JSON.parse(saved) } : this.getDefaultPreferences();
  }

  // Save notification preferences to API (with localStorage fallback for guests)
  static async saveNotificationPreferencesAsync(preferences) {
    // Also save to cache for sync access
    this._cachedPreferences = preferences;

    if (!AuthService.isAuthenticated()) {
      // Guest users: use localStorage
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
      return { success: true };
    }

    try {
      const response = await fetch(`${NotificationService.API_BASE}/api/social/notification-preferences`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ preferences })
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      return { success: false, error: 'Failed to save preferences' };
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      // Fallback to localStorage if API fails
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
      return { success: false, error: error.message };
    }
  }

  // Synchronous version for backwards compatibility
  static saveNotificationPreferences(preferences) {
    // Save to cache immediately
    this._cachedPreferences = preferences;
    // Fire async save (non-blocking)
    this.saveNotificationPreferencesAsync(preferences);
  }

  // Load and apply notification preferences
  static async loadNotificationPreferences() {
    const prefs = await this.getNotificationPreferencesAsync();
    this._cachedPreferences = prefs;

    // Schedule any pending reminders based on current preferences
    this.scheduleUpcomingReminders();

    return prefs;
  }

  // Schedule reminders for upcoming activities
  static async scheduleUpcomingReminders() {
    try {
      // Check if user is logged in
      if (!AuthService.isAuthenticated()) {
        return; // Silently bail out if not logged in
      }

      const ActivityService = require('./activityService').default;
    const prefs = this.getNotificationPreferences();

    if (!prefs.activityReminders.enabled) return;

    const scheduledActivities = await ActivityService.getStandaloneActivities();
    const now = new Date();

    scheduledActivities.forEach(activity => {
      const activityDateTime = new Date(`${activity.scheduledDate}T${activity.scheduledTime}`);
      
      // Skip past activities
      if (activityDateTime <= now) return;

      // Schedule reminders based on user preferences
      const allIntervals = [...prefs.activityReminders.intervals, ...prefs.activityReminders.customIntervals];
      
      allIntervals.forEach(interval => {
        const reminderTime = this.calculateReminderTime(activityDateTime, interval);
        
        if (reminderTime > now) {
          this.scheduleNotification(reminderTime, {
            type: 'activity_reminder',
            title: `Upcoming Activity: ${activity.title}`,
            body: `Your activity "${activity.title}" starts in ${this.formatTimeUntil(reminderTime, activityDateTime)}`,
            icon: activity.image,
            data: {
              activityId: activity.id,
              scheduledDate: activity.scheduledDate,
              type: 'reminder'
            }
          });
        }
      });
    });
    } catch (error) {
      console.warn('ActivityService not available:', error.message);
    }
  }

  // Calculate reminder time based on interval
  static calculateReminderTime(activityDateTime, interval) {
    const reminderTime = new Date(activityDateTime);
    
    switch (interval) {
      case '15min':
        reminderTime.setMinutes(reminderTime.getMinutes() - 15);
        break;
      case '30min':
        reminderTime.setMinutes(reminderTime.getMinutes() - 30);
        break;
      case '1hour':
        reminderTime.setHours(reminderTime.getHours() - 1);
        break;
      case '2hours':
        reminderTime.setHours(reminderTime.getHours() - 2);
        break;
      case '1day':
        reminderTime.setDate(reminderTime.getDate() - 1);
        break;
      case '2days':
        reminderTime.setDate(reminderTime.getDate() - 2);
        break;
      case '1week':
        reminderTime.setDate(reminderTime.getDate() - 7);
        break;
      default:
        // Handle custom intervals like "3hours", "45min", etc.
        const match = interval.match(/^(\d+)(min|hour|hours|day|days)$/);
        if (match) {
          const value = parseInt(match[1]);
          const unit = match[2];
          
          if (unit.startsWith('min')) {
            reminderTime.setMinutes(reminderTime.getMinutes() - value);
          } else if (unit.startsWith('hour')) {
            reminderTime.setHours(reminderTime.getHours() - value);
          } else if (unit.startsWith('day')) {
            reminderTime.setDate(reminderTime.getDate() - value);
          }
        }
        break;
    }
    
    return reminderTime;
  }

  // Schedule a notification
  static scheduleNotification(scheduledTime, notificationData) {
    const now = new Date();
    let delay = scheduledTime.getTime() - now.getTime();

    if (delay <= 0) return; // Past time

    // Check quiet hours
    if (this.isQuietHours(scheduledTime)) {
      // Reschedule for end of quiet hours
      const quietEnd = this.getQuietHoursEnd(scheduledTime);
      delay = quietEnd.getTime() - now.getTime();
    }

    const timerId = setTimeout(() => {
      this.showNotification(notificationData);
    }, delay);

    // Track timer for cleanup
    this.activeTimers.push(timerId);
  }

  // Show notification
  static async showNotification(notificationData) {
    if (!await this.requestPermission()) {
      console.log('Notification permission not granted');
      return;
    }

    // Store notification in history
    this.addToNotificationHistory(notificationData);

    // Show browser notification
    const notification = new Notification(notificationData.title, {
      body: notificationData.body,
      icon: '/favicon.ico', // You could use activity.image here
      badge: '/favicon.ico',
      tag: notificationData.data?.activityId || 'general',
      data: notificationData.data,
      requireInteraction: notificationData.type === 'activity_reminder'
    });

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      
      if (notificationData.data?.type === 'reminder') {
        // Navigate to activity planner
        window.location.href = '/activity-planner';
      } else if (notificationData.data?.type === 'event_discovery') {
        // Navigate to event details or search
        window.location.href = `/search-results?event=${notificationData.data.eventId}`;
      }
      
      notification.close();
    };

    // Auto-close after 10 seconds for non-critical notifications
    if (notificationData.type !== 'activity_reminder') {
      const closeTimerId = setTimeout(() => notification.close(), 10000);
      this.activeTimers.push(closeTimerId);
    }
  }

  // Check if current time is in quiet hours
  static isQuietHours(checkTime = new Date()) {
    const prefs = this.getNotificationPreferences();
    if (!prefs.quietHours.enabled) return false;

    const time = checkTime.getHours() * 60 + checkTime.getMinutes();
    const startTime = this.parseTime(prefs.quietHours.start);
    const endTime = this.parseTime(prefs.quietHours.end);

    if (startTime < endTime) {
      return time >= startTime && time < endTime;
    } else {
      // Quiet hours span midnight
      return time >= startTime || time < endTime;
    }
  }

  // Get end of quiet hours
  static getQuietHoursEnd(date) {
    const prefs = this.getNotificationPreferences();
    const endTime = this.parseTime(prefs.quietHours.end);
    const endDate = new Date(date);
    
    endDate.setHours(Math.floor(endTime / 60));
    endDate.setMinutes(endTime % 60);
    endDate.setSeconds(0);
    
    // If quiet hours end time is before current time, it's tomorrow
    if (endDate <= date) {
      endDate.setDate(endDate.getDate() + 1);
    }
    
    return endDate;
  }

  // Parse time string "HH:MM" to minutes
  static parseTime(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Format time until activity
  static formatTimeUntil(reminderTime, activityTime) {
    const diff = activityTime.getTime() - reminderTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  }

  // Send weather alert for outdoor activities
  static async sendWeatherAlert(activity, weatherData) {
    const prefs = this.getNotificationPreferences();
    if (!prefs.weatherAlerts.enabled) return;

    const isOutdoorActivity = activity.category === 'outdoor' ||
                             activity.category === 'parks' ||
                             activity.category === 'nature' ||
                             activity.tags?.includes('Outdoor');

    if (prefs.weatherAlerts.outdoorActivitiesOnly && !isOutdoorActivity) return;

    let alertMessage = '';
    let alertType = 'info';

    if (weatherData.condition?.toLowerCase().includes('rain') || weatherData.precipitation > 0.1) {
      alertMessage = `☔ Rain expected for your activity "${activity.title}" tomorrow. Consider rescheduling or bringing rain gear.`;
      alertType = 'warning';
    } else if (weatherData.windSpeed > 25) {
      alertMessage = `💨 High winds (${weatherData.windSpeed} mph) expected for "${activity.title}". Check if the activity will still run.`;
      alertType = 'warning';
    } else if (weatherData.temperature < 35 || weatherData.temperature > 90) {
      alertMessage = `🌡️ Extreme temperature (${weatherData.temperature}°F) for "${activity.title}". Dress appropriately!`;
      alertType = 'info';
    }

    if (alertMessage) {
      await this.showNotification({
        type: 'weather_alert',
        title: 'Weather Alert for Your Activity',
        body: alertMessage,
        data: {
          activityId: activity.id,
          type: 'weather',
          alertType
        }
      });
    }
  }

  // Send event discovery notification
  static async sendEventDiscoveryNotification(events) {
    const prefs = this.getNotificationPreferences();
    if (!prefs.eventDiscovery.enabled || events.length === 0) return;

    // Don't spam - limit to most relevant event
    const topEvent = events[0];

    await this.showNotification({
      type: 'event_discovery',
      title: `New Event: ${topEvent.title}`,
      body: `${topEvent.description} - ${topEvent.date}`,
      data: {
        eventId: topEvent.id,
        type: 'event_discovery'
      }
    });
  }

  // Send activity recommendation
  static async sendActivityRecommendation(activity, reason) {
    const prefs = this.getNotificationPreferences();
    if (!prefs.recommendations.enabled) return;

    await this.showNotification({
      type: 'recommendation',
      title: 'Activity Recommendation',
      body: `${reason}: "${activity.title}" - ${activity.description}`,
      data: {
        activityId: activity.id,
        type: 'recommendation'
      }
    });
  }

  // Get notification history
  static getNotificationHistory() {
    const history = localStorage.getItem('notificationHistory');
    return history ? JSON.parse(history) : [];
  }

  // Add to notification history
  static addToNotificationHistory(notificationData) {
    const history = this.getNotificationHistory();
    history.unshift({
      ...notificationData,
      timestamp: new Date().toISOString(),
      read: false
    });

    // Keep only last 50 notifications
    if (history.length > 50) {
      history.splice(50);
    }

    localStorage.setItem('notificationHistory', JSON.stringify(history));
  }

  // Mark notification as read
  static markAsRead(timestamp) {
    const history = this.getNotificationHistory();
    const notification = history.find(n => n.timestamp === timestamp);
    if (notification) {
      notification.read = true;
      localStorage.setItem('notificationHistory', JSON.stringify(history));
    }
  }

  // Get unread notification count
  static getUnreadCount() {
    const history = this.getNotificationHistory();
    return history.filter(n => !n.read).length;
  }

  // Clear all notifications
  static clearAllNotifications() {
    localStorage.removeItem('notificationHistory');
  }
}

export default NotificationService;